import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import {
  isMedicalRecordsDemoEnabled, useMedicalRecordsDocuments,
  useSyntheticMedicalRecordsPreparation, useSyntheticMedicalRecordsRequest,
} from '../hooks/useMedicalRecordsDocuments';
import type { MedicalRecordsDocumentAvailability, MedicalRecordsDocumentScope, MedicalRecordsQueueItem, MedicalRecordsSort } from '../data/medicalRecordsViews';
import { MedicalRecordsSortControl } from './MedicalRecordsSortControl';

const AvailabilityWarning: React.FC<{ availability?: MedicalRecordsDocumentAvailability | null }> = ({ availability }) => {
  if (!availability) return null;
  const missing = [
    availability.unavailableDocumentCount > 0 ? `${availability.unavailableDocumentCount} unavailable from source` : '',
    (availability.failedDocumentCount ?? 0) > 0 ? `${availability.failedDocumentCount} failed to export` : '',
  ].filter(Boolean);
  if (missing.length === 0) return null;
  return <Alert severity="warning">
    {availability.savedDocumentCount} downloaded; {missing.join('; ')}.
  </Alert>;
};

const SyntheticRequest: React.FC<{ company: string; centerId: string }> = ({ company, centerId }) => {
  const state = useSyntheticMedicalRecordsRequest(company, centerId);
  return <Box sx={{ mb: 3 }}>
    <Alert severity="info" sx={{ mb: 1 }}>
      Synthetic demo: sample HST and SNChart PDFs only. No patient record, source application, or Power Automate flow is used.
      This does not establish SNChart availability for this center.
    </Alert>
    <Button variant="outlined" onClick={state.submit} disabled={state.isLoading}>
      {state.isLoading ? 'Creating synthetic request...' : 'Create synthetic request'}
    </Button>
    {state.error && <Alert severity="error">The synthetic request could not be created. Check demo configuration and access, then retry.</Alert>}
  </Box>;
};

export const MedicalRecordsDemoRequest: React.FC<{ company: string; centerId: string }> = (props) =>
  isMedicalRecordsDemoEnabled() ? <SyntheticRequest key={`${props.company}:${props.centerId}`} {...props} /> : null;

const PrepareSyntheticDocuments: React.FC<{ scope: MedicalRecordsDocumentScope }> = ({ scope }) => {
  const state = useSyntheticMedicalRecordsPreparation(scope);
  return <>
    <Button size="small" onClick={state.submit} disabled={state.isLoading}>
      {state.isLoading ? 'Preparing demo PDFs...' : 'Prepare demo documents'}
    </Button>
    {state.error && <Alert severity="error">Preparation did not finish. Refresh the queue and retry if it is still pending.</Alert>}
  </>;
};

const Documents: React.FC<{ scope: MedicalRecordsDocumentScope }> = ({ scope }) => {
  const state = useMedicalRecordsDocuments(scope);
  const [sort, setSort] = useState<MedicalRecordsSort>({ field: 'fileName', descending: false });
  if (state.query.isFetching) return <CircularProgress aria-label="Loading documents" />;
  if (state.query.isError) return <Alert severity="error">Documents are unavailable or access was denied.</Alert>;
  const data = state.query.currentData;
  if (!data?.items.length) return <Alert severity="info">No verified documents are available.</Alert>;
  const field = sort.field === 'sourceSystem' ? 'sourceSystem' : 'fileName';
  const documents = [...data.items].sort((left, right) =>
    left[field].localeCompare(right[field]) * (sort.descending ? -1 : 1));
  return <Stack spacing={2}>
    <AvailabilityWarning availability={data.documentAvailability} />
    {data.isDemo && <Alert severity="info">Synthetic PDFs. No live retrieval or production completion is represented.</Alert>}
    <Button variant="outlined" onClick={() => state.save()} disabled={state.isDownloading}>
      Download All (ZIP)
    </Button>
    <Stack direction="row" spacing={3}>
      <MedicalRecordsSortControl field="fileName" value={sort} onChange={setSort}>Filename</MedicalRecordsSortControl>
      <MedicalRecordsSortControl field="sourceSystem" value={sort} onChange={setSort}>Source</MedicalRecordsSortControl>
    </Stack>
    {documents.map(document => <Stack key={document.documentId} direction={{ xs: 'column', sm: 'row' }}
      spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ overflowWrap: 'anywhere' }}>{document.fileName}</Typography>
        <Typography variant="caption">{document.sourceSystem} · {Math.ceil(document.sizeBytes / 1024)} KB</Typography>
      </Box>
      <Button sx={{ flexShrink: 0 }} onClick={() => state.save(document)} disabled={state.isDownloading} aria-label={`Download ${document.fileName}`}>
        Download
      </Button>
    </Stack>)}
    {state.downloadError && <Alert severity="error">The download could not be completed. Please retry.</Alert>}
    {state.downloaded && <Alert severity="success">The file was sent to your browser for download.</Alert>}
  </Stack>;
};

const ReadyDocuments: React.FC<{
  scope: MedicalRecordsDocumentScope;
  availability?: MedicalRecordsDocumentAvailability | null;
}> = ({ scope, availability }) => {
  const [open, setOpen] = useState(false);
  return <>
    <Button size="small" onClick={() => setOpen(true)}>View documents</Button>
    <AvailabilityWarning availability={availability} />
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle>Ready documents</DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>{open && <Documents scope={scope} />}</DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </>;
};

export const MedicalRecordsDocumentAction: React.FC<{ item: MedicalRecordsQueueItem }> = ({ item }) => {
  const scope = { requestId: item.requestId, company: item.company, centerId: item.centerId };
  const demoEnabled = isMedicalRecordsDemoEnabled();
  if (item.status === 'Completed') {
    if (!item.isDemo) return <ReadyDocuments scope={scope} availability={item.documentAvailability} />;
    if (!demoEnabled) return null;
    return <ReadyDocuments scope={scope} />;
  }
  if (!item.isDemo) return null;
  if (!demoEnabled) return null;
  if (['Queued', 'InProgress'].includes(item.status)) return <PrepareSyntheticDocuments scope={scope} />;
  return <Typography variant="caption">Synthetic preparation failed. Create a new demo request.</Typography>;
};
